import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MainLayout from '../components/MainLayout';
import { StatusBar } from '../components/load-workspace/StatusBar';
import '../components/load-workspace/workspace.css';
import { facilityAPI, loadAPI, interactionAPI, documentAPI } from '../api';
import { toast } from 'sonner';
import { toastApiError } from '../lib/utils';
import { StatusBadge } from '../components/StatusBadge';
import { UpdatesDialog } from '../components/load-workspace/UpdatesDialog';
import { Phone, Mail, MapPin, Copy, Upload, FileText, Eye, EyeOff, Globe, History } from 'lucide-react';

const DEFAULT_WEEKLY_SCHEDULE = {
  monday: { enabled: true, open: '08:00', close: '17:00' },
  tuesday: { enabled: true, open: '08:00', close: '17:00' },
  wednesday: { enabled: true, open: '08:00', close: '17:00' },
  thursday: { enabled: true, open: '08:00', close: '17:00' },
  friday: { enabled: true, open: '08:00', close: '17:00' },
  saturday: { enabled: false, open: '08:00', close: '17:00' },
  sunday: { enabled: false, open: '08:00', close: '17:00' },
};

// Parse receiving_hours string back to weeklySchedule state
const parseReceivingHours = (hoursString) => {
  if (!hoursString || hoursString === '24/7 Operations') {
    return DEFAULT_WEEKLY_SCHEDULE;
  }

  const newSchedule = {
    monday: { enabled: false, open: '08:00', close: '17:00' },
    tuesday: { enabled: false, open: '08:00', close: '17:00' },
    wednesday: { enabled: false, open: '08:00', close: '17:00' },
    thursday: { enabled: false, open: '08:00', close: '17:00' },
    friday: { enabled: false, open: '08:00', close: '17:00' },
    saturday: { enabled: false, open: '08:00', close: '17:00' },
    sunday: { enabled: false, open: '08:00', close: '17:00' }
  };

  const dayMap = {
    'Mon': 'monday', 'Tue': 'tuesday', 'Wed': 'wednesday',
    'Thu': 'thursday', 'Fri': 'friday', 'Sat': 'saturday', 'Sun': 'sunday'
  };
  const dayOrder = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Parse time from format like "8:00AM" to "08:00"
  const parseTime = (timeStr) => {
    const match = timeStr.match(/(\d{1,2}):(\d{2})(AM|PM)/i);
    if (!match) return '08:00';
    let [, hour, min, ampm] = match;
    hour = parseInt(hour);
    if (ampm.toUpperCase() === 'PM' && hour !== 12) hour += 12;
    if (ampm.toUpperCase() === 'AM' && hour === 12) hour = 0;
    return `${hour.toString().padStart(2, '0')}:${min}`;
  };

  // Split by comma to get each group
  const groups = hoursString.split(', ');
  groups.forEach(group => {
    // Match patterns like "Mon-Fri 8:00AM-5:00PM" or "Sat 9:00AM-1:00PM"
    const match = group.match(/^([A-Za-z,\-\s]+)\s+(\d{1,2}:\d{2}[AP]M)-(\d{1,2}:\d{2}[AP]M)$/i);
    if (!match) return;

    const [, daysStr, openTime, closeTime] = match;
    const open = parseTime(openTime);
    const close = parseTime(closeTime);

    // Handle day ranges like "Mon-Fri" or single days like "Sat"
    if (daysStr.includes('-')) {
      const [startDay, endDay] = daysStr.split('-').map(d => d.trim());
      const startIdx = dayOrder.indexOf(startDay);
      const endIdx = dayOrder.indexOf(endDay);
      if (startIdx !== -1 && endIdx !== -1) {
        for (let i = startIdx; i <= endIdx; i++) {
          const dayKey = dayMap[dayOrder[i]];
          newSchedule[dayKey] = { enabled: true, open, close };
        }
      }
    } else {
      // Single day
      const dayKey = dayMap[daysStr.trim()];
      if (dayKey) {
        newSchedule[dayKey] = { enabled: true, open, close };
      }
    }
  });

  return newSchedule;
};

const FacilityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const fileInputRef = useRef(null);
  
  const [facility, setFacility] = useState({
    name: '', address: '', city: '', state: '', zip_code: '',
    contact_phone: '', contact_email: '', receiving_hours: '',
    scheduling_method: 'Email', scheduling_email: '', scheduling_email_cc: '', scheduling_phone: '',
    portal_url: '', portal_username: '', portal_password: '', 
    portal_instructions: '', scheduling_instructions: '', notes: '',
    latitude: null, longitude: null, password_history: [], country: '',
  });
  
  const [showPassword, setShowPassword] = useState(false);
  const [updatesOpen, setUpdatesOpen] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  
  const [weeklySchedule, setWeeklySchedule] = useState(DEFAULT_WEEKLY_SCHEDULE);
  const [loads, setLoads] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [loading, setLoading] = useState(!isNew);



  const fetchFacilityData = useCallback(async () => {
    try {
      const [facilityRes, allLoads, docs, inters] = await Promise.all([
        facilityAPI.getById(id),
        loadAPI.getAll(),
        documentAPI.getByEntity('facility', id),
        interactionAPI.getByEntity('facility', id)
      ]);
      setFacility(facilityRes.data);
      
      // Parse receiving_hours into weeklySchedule state
      if (facilityRes.data.receiving_hours) {
        const parsedSchedule = parseReceivingHours(facilityRes.data.receiving_hours);
        setWeeklySchedule(parsedSchedule);
      }
      
      // Find loads that use this facility in their stops
      const facilityLoads = allLoads.data.filter(load => 
        load.stops?.some(stop => stop.facility_id === id)
      );
      setLoads(facilityLoads);
      setDocuments(docs.data);
      setInteractions(inters.data);
    } catch (error) {
      toast.error('Failed to load facility data');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (!isNew) fetchFacilityData();
  }, [id, isNew, fetchFacilityData]);

  const handleSave = async () => {
    // Generate receiving_hours string from schedule
    const receivingHoursString = formatScheduleToString(weeklySchedule);
    const facilityToSave = { ...facility, receiving_hours: receivingHoursString };
    
    try {
      if (isNew) {
        const response = await facilityAPI.create(facilityToSave);
        toast.success('Facility created successfully');
        // Navigate to the new facility's detail page
        navigate(`/facilities/${response.data.id}`);
      } else {
        await facilityAPI.update(id, facilityToSave);
        toast.success('Facility updated successfully');
        // Stay on page and refresh data
        await fetchFacilityData();
      }
    } catch (error) {
      toastApiError(toast, error, 'Facility could not be saved');
    }
  };

  const handleAddNote = async () => {
    const content = prompt('Enter note:');
    if (content) {
      try {
        await interactionAPI.create({
          entity_type: 'facility',
          entity_id: id,
          interaction_type: 'note',
          subject: 'Facility Note',
          content,
          attachments: []
        });
        toast.success('Note added');
        fetchFacilityData();
      } catch (error) {
        toast.error('Failed to add note');
      }
    }
  };

  // Generate time options for dropdowns
  const generateTimeOptions = () => {
    const times = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let min = 0; min < 60; min += 30) {
        const timeStr = `${hour.toString().padStart(2, '0')}:${min.toString().padStart(2, '0')}`;
        const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        const displayMin = min.toString().padStart(2, '0');
        times.push({ value: timeStr, label: `${displayHour}:${displayMin} ${ampm}` });
      }
    }
    return times;
  };

  const timeOptions = generateTimeOptions();

  // Format schedule to readable string
  const formatScheduleToString = (schedule) => {
    const days = [
      { key: 'monday', label: 'Mon' },
      { key: 'tuesday', label: 'Tue' },
      { key: 'wednesday', label: 'Wed' },
      { key: 'thursday', label: 'Thu' },
      { key: 'friday', label: 'Fri' },
      { key: 'saturday', label: 'Sat' },
      { key: 'sunday', label: 'Sun' }
    ];

    const formatTime = (time24) => {
      const [hour, min] = time24.split(':').map(Number);
      const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
      const ampm = hour >= 12 ? 'PM' : 'AM';
      return `${displayHour}:${min.toString().padStart(2, '0')}${ampm}`;
    };

    // Group consecutive days with same hours
    const groups = [];
    let currentGroup = null;

    days.forEach(({ key, label }) => {
      const day = schedule[key];
      if (day.enabled) {
        const hours = `${formatTime(day.open)}-${formatTime(day.close)}`;
        if (currentGroup && currentGroup.hours === hours) {
          currentGroup.days.push(label);
        } else {
          if (currentGroup) groups.push(currentGroup);
          currentGroup = { days: [label], hours };
        }
      }
    });
    if (currentGroup) groups.push(currentGroup);

    if (groups.length === 0) return '24/7 Operations';
    
    return groups.map(group => {
      const dayRange = group.days.length > 1 && 
        days.findIndex(d => d.label === group.days[0]) + group.days.length - 1 === 
        days.findIndex(d => d.label === group.days[group.days.length - 1])
        ? `${group.days[0]}-${group.days[group.days.length - 1]}`
        : group.days.join(', ');
      return `${dayRange} ${group.hours}`;
    }).join(', ');
  };

  const handleScheduleChange = (day, field, value) => {
    setWeeklySchedule(prev => ({
      ...prev,
      [day]: { ...prev[day], [field]: value }
    }));
  };

  // Document upload handlers
  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleDocumentUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      handleDocumentUpload(file);
    }
  };

  const handleDocumentUpload = async (file) => {
    if (!file) return;
    
    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }

    setUploadingDoc(true);
    try {
      const response = await documentAPI.upload(
        file,
        'facility',
        id,
        'how_to_scheduling',
        'Scheduling instructions document'
      );
      
      toast.success(`"${file.name}" uploaded successfully`);
      await fetchFacilityData();
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return <MainLayout><div className="flex items-center justify-center h-full"><div>Loading...</div></div></MainLayout>;
  }

  const set = (field) => (e) => setFacility({ ...facility, [field]: e.target.value });

  const methodColor = { Email: 'lightblue', Phone: 'lightgreen', Portal: 'purple', FCFS: 'neutral' }[facility.scheduling_method] || 'neutral';
  const howToDocs = documents.filter((doc) => doc.document_type === 'how_to_scheduling');

  const DAYS = [
    { key: 'monday', label: 'Monday' },
    { key: 'tuesday', label: 'Tuesday' },
    { key: 'wednesday', label: 'Wednesday' },
    { key: 'thursday', label: 'Thursday' },
    { key: 'friday', label: 'Friday' },
    { key: 'saturday', label: 'Saturday' },
    { key: 'sunday', label: 'Sunday' },
  ];

  const mapsQuery = facility.latitude && facility.longitude
    ? `${facility.latitude},${facility.longitude}`
    : `${facility.address}, ${facility.city}, ${facility.state} ${facility.zip_code}`;

  return (
    <MainLayout>
      <div className="lw-root" style={{ minHeight: '100%' }} data-testid="facility-detail-page">
        {/* Header */}
        <div className="lw-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <span className="lw-link" onClick={() => navigate('/facilities')} data-testid="back-button">← Facilities</span>
            <span style={{ fontSize: 18, fontWeight: 700, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {isNew ? 'New Facility' : facility.name}
            </span>
            {!isNew && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: 'var(--lw-muted)', fontSize: 13, whiteSpace: 'nowrap' }}>
                <MapPin style={{ width: 14, height: 14 }} />
                {facility.city}, {facility.state} {facility.zip_code}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {!isNew && (
              <button className="lw-btn" onClick={() => setUpdatesOpen(true)} data-testid="updates-facility-button">Updates</button>
            )}
            {!isNew && (
              <button
                className="lw-btn"
                style={{ color: 'var(--lw-red)', borderColor: 'var(--lw-red)' }}
                onClick={async () => {
                  const reason = window.prompt('Why are you deleting this facility? (required)');
                  if (reason === null) return;
                  if (!reason.trim()) { toast.error('A reason is required to delete'); return; }
                  try {
                    await facilityAPI.delete(id, reason.trim());
                    toast.success('Facility deleted');
                    navigate('/facilities');
                  } catch (e) { toast.error('Failed to delete facility'); }
                }}
                data-testid="delete-facility-button"
              >Delete</button>
            )}
            <button className="lw-btn primary" style={{ padding: '0 22px' }} onClick={handleSave} data-testid="save-facility-button">
              Save Facility
            </button>
          </div>
        </div>

        {!isNew && <UpdatesDialog open={updatesOpen} onOpenChange={setUpdatesOpen} entityType="facility" entityId={id} />}

        <div className="lw-grid" data-testid="facility-sections">
          {/* Facility Information */}
          <div className="lw-panel" data-testid="section-profile">
            <StatusBar left="Facility Information" right={isNew ? 'New' : 'Profile'} color="blue" testId="fac-statusbar-profile" />
            <div className="lw-row">
              <span className="lw-label w95">Name *</span>
              <input className="lw-input" value={facility.name} onChange={set('name')} data-testid="input-name" data-field="name" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">Address *</span>
              <input className="lw-input" value={facility.address} onChange={set('address')} data-testid="input-address" data-field="address" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">City *</span>
              <input className="lw-input" value={facility.city} onChange={set('city')} data-testid="input-city" data-field="city" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">State / ZIP *</span>
              <input className="lw-input tiny" style={{ maxWidth: 60 }} placeholder="TX" value={facility.state} onChange={set('state')} data-testid="input-state" data-field="state" />
              <input className="lw-input narrow" value={facility.zip_code} onChange={set('zip_code')} data-testid="input-zip" data-field="zip_code" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">Country</span>
              <select className="lw-input narrow" value={facility.country || ''} onChange={set('country')} data-testid="input-country" title="Used to determine cross-border route direction">
                <option value="">Auto (from state)</option>
                <option value="US">United States</option>
                <option value="MX">Mexico</option>
                <option value="CA">Canada</option>
              </select>
            </div>
            <div className="lw-row">
              <span className="lw-label w95">Time Zone</span>
              <select className="lw-input" value={facility.timezone || ''} onChange={set('timezone')} data-testid="input-timezone">
                <option value="">Auto (from state)</option>
                {['America/New_York', 'America/Chicago', 'America/Denver', 'America/Phoenix', 'America/Los_Angeles', 'America/Anchorage', 'Pacific/Honolulu',
                  'America/Mexico_City', 'America/Hermosillo', 'America/Tijuana', 'America/Mazatlan', 'America/Ciudad_Juarez', 'America/Cancun',
                  'America/Toronto', 'America/Winnipeg', 'America/Regina', 'America/Edmonton', 'America/Vancouver', 'America/Halifax'].map(tz => (
                  <option key={tz} value={tz}>{tz}</option>
                ))}
              </select>
            </div>
            <div className="lw-sep" />
            <div className="lw-row">
              <span className="lw-label w95">Phone *</span>
              <input className="lw-input" value={facility.contact_phone || ''} onChange={set('contact_phone')} data-testid="input-phone" data-field="contact_phone" />
              {facility.contact_phone && (
                <a href={`tel:${facility.contact_phone.replace(/[^0-9+]/g, '')}`} className="lw-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }} data-testid="call-phone-button">
                  <Phone style={{ width: 13, height: 13 }} /> Call
                </a>
              )}
            </div>
            <div className="lw-row">
              <span className="lw-label w95">Email *</span>
              <input className="lw-input" type="email" value={facility.contact_email || ''} onChange={set('contact_email')} data-testid="input-email" data-field="contact_email" />
              {facility.contact_email && (
                <a href={`mailto:${facility.contact_email}?subject=Inquiry - ${facility.name}`} className="lw-btn" style={{ display: 'inline-flex', alignItems: 'center', gap: 4, textDecoration: 'none' }} data-testid="email-button">
                  <Mail style={{ width: 13, height: 13 }} /> Email
                </a>
              )}
            </div>
            <div className="lw-sep" />
            <div className="lw-row">
              <span className="lw-label w95">Latitude</span>
              <input className="lw-input" type="number" step="any" placeholder="32.7767" value={facility.latitude ?? ''} onChange={(e) => setFacility({ ...facility, latitude: e.target.value ? parseFloat(e.target.value) : null })} data-testid="input-latitude" />
            </div>
            <div className="lw-row">
              <span className="lw-label w95">Longitude</span>
              <input className="lw-input" type="number" step="any" placeholder="-96.7970" value={facility.longitude ?? ''} onChange={(e) => setFacility({ ...facility, longitude: e.target.value ? parseFloat(e.target.value) : null })} data-testid="input-longitude" />
            </div>
            <p style={{ fontSize: 12, color: 'var(--lw-muted)' }}>
              Coordinates are geocoded automatically from the address. Override them here for a precise map pin.
            </p>
            <div className="lw-btnrow">
              <a className="lw-btn" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, textDecoration: 'none' }} href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(mapsQuery)}`} target="_blank" rel="noopener noreferrer" data-testid="open-google-maps">
                <Globe style={{ width: 13, height: 13 }} /> Google Maps
              </a>
              <a className="lw-btn" style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 4, textDecoration: 'none' }} href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(mapsQuery)}`} target="_blank" rel="noopener noreferrer" data-testid="get-directions">
                <MapPin style={{ width: 13, height: 13 }} /> Directions
              </a>
            </div>
          </div>

          {/* Receiving Hours */}
          <div className="lw-panel" data-testid="section-hours">
            <StatusBar left="Receiving Hours" right="Weekly" color="lightblue" testId="fac-statusbar-hours" />
            {DAYS.map(({ key, label }) => (
              <div className="lw-row" key={key}>
                <input
                  type="checkbox"
                  className="lw-checkbox"
                  checked={weeklySchedule[key].enabled}
                  onChange={(e) => handleScheduleChange(key, 'enabled', e.target.checked)}
                  data-testid={`checkbox-${key}`}
                />
                <span className="lw-label w95">{label}</span>
                {weeklySchedule[key].enabled ? (
                  <>
                    <select className="lw-input" value={weeklySchedule[key].open} onChange={(e) => handleScheduleChange(key, 'open', e.target.value)} data-testid={`select-${key}-open`}>
                      {timeOptions.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                    <span style={{ fontSize: 12, color: 'var(--lw-muted)' }}>to</span>
                    <select className="lw-input" value={weeklySchedule[key].close} onChange={(e) => handleScheduleChange(key, 'close', e.target.value)} data-testid={`select-${key}-close`}>
                      {timeOptions.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </>
                ) : (
                  <span style={{ fontSize: 12.5, color: 'var(--lw-muted)', fontStyle: 'italic' }}>Closed</span>
                )}
              </div>
            ))}
            <div className="lw-sep" />
            <div className="lw-profitbox" style={{ fontSize: 12.5, fontWeight: 600 }} data-testid="hours-preview">
              <span>Preview</span>
              <span style={{ textAlign: 'right', whiteSpace: 'normal' }}>{formatScheduleToString(weeklySchedule)}</span>
            </div>
          </div>

          {/* Scheduling Method */}
          <div className="lw-panel" data-testid="section-scheduling">
            <StatusBar
              left="Scheduling Method"
              right={
                <select
                  className="lw-input"
                  style={{ height: 24, maxWidth: 150, fontSize: 12, fontWeight: 700 }}
                  value={facility.scheduling_method || 'FCFS'}
                  onChange={set('scheduling_method')}
                  onClick={(e) => e.stopPropagation()}
                  data-testid="select-scheduling-method"
                >
                  <option value="Email">Email</option>
                  <option value="Phone">Phone</option>
                  <option value="Portal">Portal</option>
                  <option value="FCFS">FCFS</option>
                </select>
              }
              color={methodColor}
              testId="fac-statusbar-scheduling"
            />
            {facility.scheduling_method === 'Email' && (
              <>
                <div className="lw-row">
                  <span className="lw-label w95">To Email(s) *</span>
                  <input className="lw-input" value={facility.scheduling_email || ''} onChange={set('scheduling_email')} placeholder="appointments@facility.com, ..." data-testid="input-scheduling-email" />
                </div>
                <div className="lw-row">
                  <span className="lw-label w95">CC Email(s)</span>
                  <input className="lw-input" value={facility.scheduling_email_cc || ''} onChange={set('scheduling_email_cc')} placeholder="manager@facility.com, ..." data-testid="input-scheduling-email-cc" />
                </div>
              </>
            )}
            {facility.scheduling_method === 'Phone' && (
              <div className="lw-row">
                <span className="lw-label w95">Phone *</span>
                <input className="lw-input" value={facility.scheduling_phone || ''} onChange={set('scheduling_phone')} placeholder="(555) 123-4567" data-testid="input-scheduling-phone" />
              </div>
            )}
            {facility.scheduling_method === 'Portal' && (
              <>
                <div className="lw-row">
                  <span className="lw-label w95">Portal URL *</span>
                  <input className="lw-input" type="url" value={facility.portal_url || ''} onChange={set('portal_url')} placeholder="https://facility.scheduler.com" data-testid="input-portal-url" />
                </div>
                <div className="lw-row">
                  <span className="lw-label w95">Username</span>
                  <input className="lw-input" value={facility.portal_username || ''} onChange={set('portal_username')} data-testid="input-portal-username" />
                  <button className="lw-btn" style={{ padding: '0 8px' }} onClick={() => { navigator.clipboard.writeText(facility.portal_username || ''); toast.success('Username copied!'); }} title="Copy username" data-testid="copy-username-button">
                    <Copy style={{ width: 13, height: 13 }} />
                  </button>
                </div>
                <div className="lw-row">
                  <span className="lw-label w95">Password</span>
                  <input className="lw-input" type={showPassword ? 'text' : 'password'} value={facility.portal_password || ''} onChange={set('portal_password')} placeholder="••••••••" data-testid="input-portal-password" />
                  <button className="lw-btn" style={{ padding: '0 8px' }} onClick={() => setShowPassword(!showPassword)} title={showPassword ? 'Hide password' : 'Show password'} data-testid="toggle-password-visibility">
                    {showPassword ? <EyeOff style={{ width: 13, height: 13 }} /> : <Eye style={{ width: 13, height: 13 }} />}
                  </button>
                  <button className="lw-btn" style={{ padding: '0 8px' }} onClick={() => { navigator.clipboard.writeText(facility.portal_password || ''); toast.success('Password copied!'); }} title="Copy password" data-testid="copy-password-button">
                    <Copy style={{ width: 13, height: 13 }} />
                  </button>
                </div>
              </>
            )}
            {facility.scheduling_method !== 'FCFS' && (
              <div className="lw-row" style={{ alignItems: 'flex-start' }}>
                <span className="lw-label w95" style={{ paddingTop: 5 }}>Instructions</span>
                <textarea className="lw-input" rows={3} value={facility.scheduling_instructions || ''} onChange={set('scheduling_instructions')} placeholder="How to schedule with this facility..." data-testid="input-scheduling-instructions" />
              </div>
            )}
            {facility.scheduling_method === 'Portal' && (
              <div className="lw-row" style={{ alignItems: 'flex-start' }}>
                <span className="lw-label w95" style={{ paddingTop: 5 }}>Portal Notes</span>
                <textarea className="lw-input" rows={2} value={facility.portal_instructions || ''} onChange={set('portal_instructions')} placeholder="Navigation tips, troubleshooting..." data-testid="input-portal-instructions" />
              </div>
            )}
            {facility.scheduling_method === 'FCFS' && (
              <p style={{ fontSize: 12.5, color: 'var(--lw-muted)' }} data-testid="fcfs-info">
                First come, first served — no appointment needed. Drivers arrive during receiving hours: <strong>{facility.receiving_hours || 'see profile'}</strong>
              </p>
            )}
            {facility.password_history && facility.password_history.length > 0 && (
              <>
                <div className="lw-sep" />
                <div className="lw-row" style={{ marginBottom: 2 }}>
                  <History style={{ width: 13, height: 13, color: 'var(--lw-muted)' }} />
                  <span className="lw-label" style={{ fontSize: 12 }}>Password Change History</span>
                </div>
                <div style={{ maxHeight: 90, overflowY: 'auto' }}>
                  {facility.password_history.slice().reverse().map((record, idx) => (
                    <div key={`${record.changed_at}-${idx}`} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--lw-muted)', padding: '2px 0' }}>
                      <span>By <strong>{record.changed_by_name}</strong></span>
                      <span>{new Date(record.changed_at).toLocaleString()}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          {/* Appointment & Dock Rules */}
          <div className="lw-panel" data-testid="section-rules">
            <StatusBar left="Appointment & Dock Rules" right="Gate Info" color="yellow" testId="fac-statusbar-rules" />
            <textarea
              className="lw-input"
              rows={8}
              value={facility.notes || ''}
              onChange={set('notes')}
              placeholder="Dock doors 1-20 for dry freight, lumping fees, security clearance, driver check-in procedures..."
              data-testid="input-notes"
            />
            <p style={{ fontSize: 12, color: 'var(--lw-muted)', marginTop: 6 }}>
              Include: dock assignments, lumper fees, equipment requirements, security protocols, temperature control, check-in steps.
            </p>
          </div>

          {/* Location Map */}
          {!isNew && facility.address && (
            <div className="lw-panel" data-testid="section-map">
              <StatusBar left="Location" right={facility.latitude ? 'Precise Pin' : 'Address Search'} color={facility.latitude ? 'lightgreen' : 'neutral'} testId="fac-statusbar-map" />
              <iframe
                title="Facility Location Map"
                width="100%"
                style={{ border: '1px solid var(--lw-border)', borderRadius: 4, flexGrow: 1, minHeight: 320 }}
                src={`https://maps.google.com/maps?q=${encodeURIComponent(mapsQuery)}&output=embed${facility.latitude ? '&z=15' : ''}`}
                allowFullScreen
                loading="lazy"
                data-testid="google-map"
              />
              <p style={{ fontSize: 11.5, color: 'var(--lw-muted)', marginTop: 5 }}>
                📍 {facility.latitude && facility.longitude ? `${facility.latitude}, ${facility.longitude}` : 'No coordinates yet — save to auto-geocode'}
              </p>
            </div>
          )}

          {/* How-to Documents */}
          {!isNew && (
            <div className="lw-panel" data-testid="section-documents">
              <StatusBar left="How-To Documents" right={`${howToDocs.length} file${howToDocs.length === 1 ? '' : 's'}`} color="neutral" testId="fac-statusbar-docs" />
              <div
                className={isDragging ? 'lw-profitbox' : ''}
                style={{ border: `2px dashed ${isDragging ? 'var(--lw-primary)' : 'var(--lw-border-strong)'}`, borderRadius: 4, padding: 14, textAlign: 'center', marginBottom: 8, background: isDragging ? '#fff0f4' : '#f9fafb' }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                data-testid="doc-drop-zone"
              >
                <Upload style={{ width: 20, height: 20, margin: '0 auto 6px', color: 'var(--lw-muted)' }} />
                <p style={{ fontSize: 12.5, color: 'var(--lw-muted)' }}>{isDragging ? 'Drop file here...' : 'Drag & drop, or use Add Document'}</p>
                <p style={{ fontSize: 11, color: 'var(--lw-muted)' }}>PDF, Images, Word, Excel — max 10MB</p>
              </div>
              <input type="file" ref={fileInputRef} onChange={handleFileSelect} className="hidden" style={{ display: 'none' }} accept=".pdf,.doc,.docx,.png,.jpg,.jpeg,.gif,.txt,.xlsx,.xls" data-testid="file-input-how-to" />
              <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                {howToDocs.length === 0 ? (
                  <p style={{ fontSize: 12.5, color: 'var(--lw-muted)', textAlign: 'center', padding: '10px 0' }}>No documents attached yet</p>
                ) : (
                  howToDocs.map((doc) => (
                    <div key={doc.id} className="lw-row" style={{ border: '1px solid var(--lw-border)', borderRadius: 4, padding: 6 }} data-testid={`how-to-doc-${doc.id}`}>
                      <FileText style={{ width: 15, height: 15, color: 'var(--lw-primary)', flexShrink: 0 }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <p style={{ fontSize: 12.5, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{doc.file_name}</p>
                        <p style={{ fontSize: 11, color: 'var(--lw-muted)' }}>Added {new Date(doc.created_at).toLocaleDateString()}</p>
                      </div>
                      <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="lw-link" data-testid={`view-doc-${doc.id}`}>View</a>
                      <button
                        className="lw-btn"
                        style={{ height: 24, padding: '0 8px', color: 'var(--lw-red)' }}
                        onClick={() => {
                          if (window.confirm('Delete this document?')) {
                            documentAPI.delete(doc.id).then(() => { toast.success('Document deleted'); fetchFacilityData(); }).catch(() => toast.error('Failed to delete'));
                          }
                        }}
                        data-testid={`delete-doc-${doc.id}`}
                      >✕</button>
                    </div>
                  ))
                )}
              </div>
              <div className="lw-btnrow">
                <button className="lw-btn primary" onClick={() => fileInputRef.current?.click()} disabled={uploadingDoc} data-testid="add-how-to-document">
                  {uploadingDoc ? 'Uploading...' : '+ Add Document'}
                </button>
              </div>
            </div>
          )}

          {/* Load History */}
          {!isNew && (
            <div className="lw-panel" data-testid="section-loads">
              <StatusBar left="Load History" right={`${loads.length} load${loads.length === 1 ? '' : 's'}`} color="lightgreen" testId="fac-statusbar-loads" />
              <div style={{ overflowY: 'auto', overflowX: 'auto', flexGrow: 1 }}>
                <table className="lw-fin-table" style={{ fontSize: 12.5 }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Load</th>
                      <th style={{ textAlign: 'left' }}>Customer</th>
                      <th style={{ textAlign: 'left' }}>Type</th>
                      <th style={{ textAlign: 'left' }}>Status</th>
                      <th style={{ textAlign: 'left' }}>Appointment</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loads.length === 0 ? (
                      <tr><td colSpan="5" style={{ textAlign: 'center', padding: '14px 0', color: 'var(--lw-muted)' }}>No loads yet</td></tr>
                    ) : (
                      loads.map((load) => {
                        const stop = load.stops.find((s) => s.facility_id === id);
                        return (
                          <tr key={load.id} data-testid={`load-${load.id}`}>
                            <td>
                              <span className="lw-link" onClick={() => navigate(`/loads/${load.id}`)}>
                                {load.load_number || load.id.substring(0, 8)}
                              </span>
                            </td>
                            <td style={{ fontWeight: 400, maxWidth: 140, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{load.customer_name}</td>
                            <td style={{ fontWeight: 700, color: stop?.type === 'pickup' ? '#0a3a75' : '#0c4a25' }}>{stop?.type?.toUpperCase()}</td>
                            <td><StatusBadge status={load.status} /></td>
                            <td style={{ fontWeight: 400, whiteSpace: 'nowrap' }}>
                              {stop?.appointment_datetime ? new Date(stop.appointment_datetime).toLocaleString() : <span style={{ color: 'var(--lw-muted)' }}>Not scheduled</span>}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Notes & Interactions */}
          {!isNew && (
            <div className="lw-panel" data-testid="section-notes">
              <StatusBar left="Notes & Interactions" right={`${interactions.length}`} color="neutral" testId="fac-statusbar-notes" />
              <div style={{ overflowY: 'auto', flexGrow: 1 }}>
                {interactions.length === 0 ? (
                  <p style={{ fontSize: 12.5, color: 'var(--lw-muted)', textAlign: 'center', padding: '14px 0' }}>No interactions yet</p>
                ) : (
                  interactions.map((inter) => (
                    <div key={inter.id} style={{ border: '1px solid var(--lw-border)', borderRadius: 4, padding: 8, marginBottom: 6 }} data-testid={`interaction-${inter.id}`}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                        <span style={{ fontSize: 12.5, fontWeight: 700 }}>{inter.subject}</span>
                        <span style={{ fontSize: 11, color: 'var(--lw-muted)' }}>{new Date(inter.created_at).toLocaleString()}</span>
                      </div>
                      <p style={{ fontSize: 12.5 }}>{inter.content}</p>
                      <p style={{ fontSize: 11, color: 'var(--lw-muted)', marginTop: 3 }}>By: {inter.created_by_name}</p>
                    </div>
                  ))
                )}
              </div>
              <div className="lw-btnrow">
                <button className="lw-btn primary" onClick={handleAddNote} data-testid="add-note-button">+ Add Note</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
};

export default FacilityDetail;
